import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Box } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import FormBase from 'components/forms/new/FormBase';
import { useUpdateRule, useGetRule, useGetDialPlan } from './queries';
import { ruleSchema } from './schema';
import RuleNameField from './formFields/RuleNameField';
import RulePriorityField from './formFields/RulePriorityField';
import RulePatternField from './formFields/RulePatternField';
import RuleTranslationField from './formFields/RuleTranslationField';
import RulesDropdown from './components/RulesDropdown';
import {
    LoadingIndicator,
    Alert,
    AlertIcon,
    AlertDescription,
} from 'components/v4';
import useFieldValidation from 'hooks/useFieldValidation';
import { formContainerStyles } from './constants';

const RuleEdit = () => {
    const { dialPlanId, ruleId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { darkMode } = useSelector((state) => state.settings);
    const { data: dialPlan, isLoading: dialPlanLoading } =
        useGetDialPlan(dialPlanId);
    const { data: rule, isLoading: ruleLoading } = useGetRule(ruleId);
    const { mutate: updateRule, isLoading: isUpdating } =
        useUpdateRule(dialPlanId);
    const [error, setError] = useState(null);
    const [modalState, setModalState] = useState({
        name: '',
        priority: '',
        pattern: '',
        translation: '',
    });

    useEffect(() => {
        if (rule) {
            setModalState({
                name: rule.name,
                priority: rule.priority,
                pattern: rule.pattern,
                translation: rule.translation,
            });
        }
    }, [rule]);

    const breadCrumbTrail = [
        { label: 'Dial Plans', path: '/dial-plans' },
        {
            label: dialPlan?.name || 'Dial Plan',
            path: `/dial-plans/${dialPlanId}${location.search}`,
        },
        {
            label: (
                <RulesDropdown dialPlanId={dialPlanId} currentRuleId={ruleId} />
            ),
            path: undefined,
        },
    ];

    const { validatefn } = useFieldValidation(modalState, ruleSchema);

    const handleSubmit = (values, actions) => {
        if (!validatefn()) {
            actions.setSubmitting(false);
            return;
        }

        setError(null);
        updateRule(
            { ...rule, ...values },
            {
                onSuccess: () => {
                    navigate(`/dial-plans/${dialPlanId}${location.search}`);
                },
                onError: (err) => {
                    setError(
                        err?.response?.data?.message || 'An error occurred.',
                    );
                    actions.setSubmitting(false);
                },
            },
        );
    };

    if (dialPlanLoading || ruleLoading || !rule) return <LoadingIndicator />;

    return (
        <Box sx={formContainerStyles.noHorizontalDivider}>
            <FormBase
                breadCrumbTrail={breadCrumbTrail}
                initialValues={{
                    name: rule.name,
                    priority: rule.priority,
                    pattern: rule.pattern,
                    translation: rule.translation,
                }}
                enableReinitialize
                finalFormSubmit={handleSubmit}
                backLink={`/dial-plans/${dialPlanId}${location.search}`}
                isLoading={isUpdating}>
                {(formProps) => {
                    const { errors, touched, handleChange } = formProps;

                    const handleFieldChange = (e) => {
                        handleChange(e);
                        setModalState((prev) => ({
                            ...prev,
                            [e.target.name]: e.target.value,
                        }));
                    };

                    return [
                        <RuleNameField
                            key="name"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                            onChange={handleFieldChange}
                        />,
                        <RulePriorityField
                            key="priority"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                            onChange={handleFieldChange}
                        />,
                        <RulePatternField
                            key="pattern"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                            onChange={handleFieldChange}
                        />,
                        <RuleTranslationField
                            key="translation"
                            darkMode={darkMode}
                            errors={errors}
                            touched={touched}
                            onChange={handleFieldChange}
                        />,
                        error && (
                            <Box key="error-box" paddingBottom="0.5rem">
                                <Alert
                                    status="error"
                                    mt={4}
                                    marginBottom="1rem">
                                    <AlertIcon />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            </Box>
                        ),
                    ];
                }}
            </FormBase>
        </Box>
    );
};

export default RuleEdit;
